import {
    Button,
    CircularProgress,
    Grid,
    makeStyles,
    Typography
} from "@material-ui/core";
import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomAlert from "../../componentes/alerts/CustomAlert";
import { DialogConformationGeneric } from "../../componentes/dialog/DialogConfirmationGeneric";
import GridInputRow from "../../componentes/form-grids/GridInputRow";
import ProductMotulImageCarousel from "./ProductMotulImageCarousel";
import ProductMotulImageService from "./ProductMotulImageService";
import { PRODUCT_MOTUL_IMAGE_LIMIT } from "./productMotulImageConfig";
import { validateProductMotulImageSelection } from "./productMotulImageUtils";

const useStyles = makeStyles((theme) => ({
    root: {
        border: "1px solid #d9d9d9",
        borderRadius: 8,
        marginTop: theme.spacing(2),
        padding: theme.spacing(2)
    },
    header: {
        alignItems: "center",
        display: "flex",
        justifyContent: "space-between",
        marginBottom: theme.spacing(1)
    },
    counter: {
        color: "#6b6b6b"
    },
    hiddenInput: {
        display: "none"
    },
    toolbar: {
        alignItems: "center",
        display: "flex",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2)
    },
    helperText: {
        color: "#6b6b6b",
        marginBottom: theme.spacing(1)
    }
}));

const ProductMotulImagesPanel = ({ productId, canEdit }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [deleting, setDeleting] = useState(false);
    const [alertState, setAlertState] = useState({
        error: false,
        success: false,
        message: ""
    });
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        image: null
    });

    const closeAlert = () => setAlertState({ error: false, success: false, message: "" });

    const loadImages = () => {
        if (!productId) {
            setImages([]);
            return Promise.resolve();
        }

        setLoading(true);
        return ProductMotulImageService.getImages(productId)
            .then((response) => {
                setImages(Array.isArray(response.data) ? response.data : []);
            })
            .catch(() => {
                setImages([]);
                setAlertState({
                    error: true,
                    success: false,
                    message: t("productMotulImagesLoadError")
                });
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        closeAlert();
        loadImages();
    }, [productId]);

    const resetInput = () => {
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    const handleSelectFiles = async (event) => {
        const selectedFiles = event.target.files;
        const validationMessage = validateProductMotulImageSelection({
            files: selectedFiles,
            persistedCount: images.length,
            pendingCount: uploadingCount,
            t
        });

        if (validationMessage) {
            setAlertState({
                error: true,
                success: false,
                message: validationMessage
            });
            resetInput();
            return;
        }

        const files = Array.from(selectedFiles || []);
        if (!files.length) {
            return;
        }

        setUploadingCount(files.length);
        closeAlert();

        for (const file of files) {
            const formData = new FormData();
            formData.append("UploadFiles", file);

            try {
                debugger;
                await ProductMotulImageService.uploadImage(productId, formData);
            } catch (error) {
                setAlertState({
                    error: true,
                    success: false,
                    message: error?.response?.data || t("productMotulImagesSaveError")
                });
            }
        }

        setUploadingCount(0);
        resetInput();
        loadImages();
    };

    const handleDeleteConfirmation = (confirmed) => {
        if (!confirmed || !deleteDialog.image) {
            setDeleteDialog({ open: false, image: null });
            return;
        }

        setDeleting(true);
        ProductMotulImageService.deleteImage(productId, deleteDialog.image.id)
            .then(() => {
                setAlertState({
                    error: false,
                    success: true,
                    message: t("productMotulImagesDeleteSuccess")
                });
                return loadImages();
            })
            .catch(() => {
                setAlertState({
                    error: true,
                    success: false,
                    message: t("productMotulImagesDeleteError")
                });
            })
            .finally(() => {
                setDeleting(false);
                setDeleteDialog({ open: false, image: null });
            });
    };

    const totalCount = images.length + uploadingCount;
    const canUpload = canEdit && productId > 0 && totalCount < PRODUCT_MOTUL_IMAGE_LIMIT;

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Typography variant="h6">{t("productMotulImagesTitle")}</Typography>
                <Typography className={classes.counter}>
                    {`${images.length} / ${PRODUCT_MOTUL_IMAGE_LIMIT} ${t("productMotulImagesCounterSuffix")}`}
                </Typography>
            </div>

            <Typography className={classes.helperText}>
                {productId ? t("productMotulImagesHelper") : t("productMotulImagesSaveProductFirst")}
            </Typography>

            <CustomAlert
                error={alertState.error}
                success={alertState.success}
                handleCloseAlert={closeAlert}
                message={alertState.message}
                snackbar
                autoClose
            />

            <div className={classes.toolbar}>
                <input
                    accept=".jpg,.jpeg,.png"
                    className={classes.hiddenInput}
                    multiple
                    onChange={handleSelectFiles}
                    ref={inputRef}
                    type="file"
                />
                <Button
                    color="primary"
                    disabled={!canUpload}
                    onClick={() => inputRef.current?.click()}
                    startIcon={<AddPhotoAlternateIcon />}
                    variant="contained"
                >
                    {t("productMotulImagesAdd")}
                </Button>

                {uploadingCount > 0 ? <CircularProgress size={20} /> : null}
                {images.length >= PRODUCT_MOTUL_IMAGE_LIMIT ? (
                    <Typography color="error">{t("productMotulImagesLimit")}</Typography>
                ) : null}
            </div>

            {loading ? (
                <GridInputRow>
                    <Grid item>
                        <CircularProgress size={24} />
                    </Grid>
                </GridInputRow>
            ) : (
                <ProductMotulImageCarousel
                    images={images}
                    isDeleting={deleting}
                    onDelete={canEdit ? (image) => setDeleteDialog({ open: true, image }) : null}
                />
            )}

            <DialogConformationGeneric
                open={deleteDialog.open}
                handleClose={handleDeleteConfirmation}
                message="productMotulImagesDeleteConfirmation"
            />
        </div>
    );
};

export default ProductMotulImagesPanel;
